import type { Chart, LoadDataType } from 'klinecharts'
import type { DataFeed } from '../types/datafeed'
import type { EdgeSymbol, EdgeBar } from '../types/chart'
import type { Period, RegularSessionBuckets } from '../utils/resolution'
import {
  INITIAL_PAGE_BARS,
  LOOKBACK_BY_PERIOD,
  periodKeyOf,
  isDailyPeriod,
  isIntradayPeriod,
} from '../utils/resolution'
import { createRegularSessionFilter } from '../utils/sessionFilter'
import { isPremarketFormingDailyBar } from '../utils/premarketDailyBar'
import type { EventBus } from './EventBus'

/**
 * Mirror of the runtime values of klinecharts' `LoadDataType` enum.
 *
 * Why: importing `{ LoadDataType }` from klinecharts at runtime works in
 * production (via the UMD bundle) but resolves to `undefined` under Vitest's
 * ESM-from-CJS interop when the package is loaded from `index.cjs` →
 * `umd/klinecharts.js`. Comparing against the stable string values that the
 * enum is declared with avoids the interop landmine without changing
 * semantics.
 *
 * Source: klinecharts/dist/index.esm.js — `LoadDataType['Init']='init'`,
 * `'Forward'='forward'`, `'Backward'='backward'`.
 */
const LOAD_DATA_TYPE: Record<'Init' | 'Forward' | 'Backward', LoadDataType> = {
  Init: 'init' as LoadDataType,
  Forward: 'forward' as LoadDataType,
  Backward: 'backward' as LoadDataType,
}

/** Per-ticker floor metadata returned by `EdgeDataFeed.getFloor()`. */
interface FloorPayload {
  ticker: string
  earliestDate: string | null
  ipoDate: string | null
  latestDate?: string | null
}

/**
 * Optional floor-lookup capability on the datafeed. `EdgeDataFeed` implements
 * this; mock datafeeds in tests opt-in by attaching a `getFloor` method.
 * Kept as a structural narrowing here so we don't have to widen the
 * `DataFeed` interface in `types/datafeed.ts` for a single optional method.
 */
interface FloorCapableDataFeed {
  getFloor(ticker: string): Promise<FloorPayload | null>
}

function hasGetFloor(df: DataFeed): df is DataFeed & FloorCapableDataFeed {
  return typeof (df as Partial<FloorCapableDataFeed>).getFloor === 'function'
}

/** Resolve a floor timestamp from an ISO `YYYY-MM-DD` string, or null. */
function parseIsoToMs(s: string | null | undefined): number | null {
  if (!s) return null
  const parsed = Date.parse(s)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Resolve the floor timestamp from a floor payload. Resolution order:
 *   earliestDate → ipoDate → null.
 */
function resolveFloorMs(payload: FloorPayload | null): number | null {
  if (!payload) return null
  return parseIsoToMs(payload.earliestDate) ?? parseIsoToMs(payload.ipoDate) ?? null
}

/**
 * Minimum calendar-time floor for intraday fetch windows, in ms.
 *
 * Guarantees the initial window reaches back past the longest plausible
 * market closure (4-day Thanksgiving / Memorial Day weekend ≈ 3.8 days) plus
 * enough margin to land inside multiple completed trading sessions, so the
 * backend always returns bars on first load — even when the chart is opened
 * off-hours, on a weekend, or the morning after a holiday. Without this
 * floor, sub-5m periods could request a window that falls entirely on
 * closed-market clock time, get zero bars back, and trip
 * `evaluateInitialExhaustion` → the first-bar history marker.
 *
 * 7 days = ~3 RTH sessions captured even after a max-length holiday gap
 * (4-day gap before a fresh Tuesday open still leaves 3 prior sessions in
 * the window). Daily+ periods already exceed this floor by orders of
 * magnitude, so it only ever matters for 1m / 2m / 3m.
 *
 * Trade-off: scroll-back pages for sub-5m periods now fetch ~600 bars per
 * page instead of ~500 — negligible cost, simpler scroll behaviour.
 */
const MIN_INTRADAY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Minimum window for a DAILY page: five calendar years, about 1,260 sessions.
 *
 * Our EMA is SMA-seeded, so its seed fades as (1 - 2/(n+1))^(bars past the
 * seed). At 500 bars x 1.5 (750 days, ~516 sessions) an EMA(200) still carries
 * ~4% of its seed, enough to show in the legend's cents; at five years it is
 * 0.003%. The 1D/1W timeframe on intraday charts (MTF Phase B) fetches this
 * same window through `fetchPeriodWindow`, so both draw the same 200-day line
 * and one request serves the 1D chart, the preload and the MTF series.
 */
export const MIN_DAILY_WINDOW_MS = Math.ceil(5 * 365.25 * 24 * 60 * 60 * 1000)

/**
 * Compute the page-size window (in ms) for a fetch on `period`. Used by both
 * the initial `loadHistory` fetch and each scroll-back page.
 *
 * The window is `INITIAL_PAGE_BARS × period.ms` inflated to compensate for
 * non-trading clock-time, then floored at `MIN_INTRADAY_WINDOW_MS` for
 * intraday periods so the window reliably contains bars even when it spans
 * weekends or holidays.
 *
 * Inflation:
 * - intraday (`minute` / `hour`): 5× — covers RTH-only data through long
 *   weekends. Trading is only ~19% of clock time (6.5h / 24h × 5d / 7d).
 * - daily+ (`day` / `week` / `month` / `year`): 1.5× — weekends and holidays
 *   make trading ~71% of calendar days; 1.5× has plenty of headroom.
 *
 * Intraday floor:
 * - For sub-5m periods the bar-count × 5× window falls short of one max
 *   holiday gap (1m's ~1.7-day window can land entirely on a closed market).
 *   The `MIN_INTRADAY_WINDOW_MS` floor guarantees at least ~3 trading
 *   sessions in the window regardless of when the chart is opened.
 *
 * Daily floor:
 * - 1D pages span at least `MIN_DAILY_WINDOW_MS` (five years), so a 200-day
 *   EMA has warmed up on the first page. Weekly and monthly keep the formula.
 *
 * Spec: docs/superpowers/specs/2026-05-05-chart-fetch-volume-redesign-design.md
 */
export function pageMsFromBars(period: Period): number {
  const baseMs = INITIAL_PAGE_BARS * period.ms
  const isIntraday = isIntradayPeriod(period)
  const inflate = isIntraday ? 5 : 1.5
  const inflated = Math.ceil(baseMs * inflate)
  if (isIntraday) return Math.max(inflated, MIN_INTRADAY_WINDOW_MS)
  return isDailyPeriod(period) ? Math.max(inflated, MIN_DAILY_WINDOW_MS) : inflated
}

/**
 * Soft cap on the number of bars klinecharts holds for the active
 * `(ticker, period)`. Once `chart.getDataList().length` reaches this value,
 * the scroll-back loop stops firing further `Forward` callbacks — the user
 * sees what's already loaded and a switch (period / ticker) clears the
 * buffer back to zero via `chart.clearData()`.
 *
 * Sized so the deepest-resolution period stays bounded:
 * - 1m at 50k bars ≈ 128 RTH trading days (~6 months) → ~3.2 MB at 64 B/bar.
 * - 5m, 15m, 1H scale proportionally further into history.
 * - 1D at 50k = 198 years (effectively unbounded for daily).
 *
 * Cap is per-(ticker, period) automatically: `setSymbol` and `setPeriod`
 * both call `chart.clearData()`, so the in-memory buffer never spans
 * multiple chart views.
 */
export const MAX_BARS_PER_CHART = 50_000

/**
 * How many times klinecharts may re-fire `Forward` for the SAME leftmost after
 * a scroll-back attempt that produced nothing usable — a thrown fetch, a
 * transient empty response, or a genuine multi-day data gap above the floor —
 * before we leave the re-fire guard set and let it stop. Without this, a single
 * blip would freeze scroll-back at that spot forever (the guard suppresses the
 * retry); with it unbounded, a persistent failure would become a tight retry
 * loop. A small budget covers the blip case without inviting a storm. Reset
 * per-(symbol, period) by `setSymbol` / `setPeriod` / `reload`.
 */
const MAX_SCROLLBACK_EMPTY_RETRIES = 2
/**
 * Scroll-back pages the layer fetches on its own, back to back, when a page
 * lands and the viewport is still short. klinecharts re-asks for older bars
 * synchronously from inside our delivery whenever the leftmost bar is still
 * at index 0; on a thin symbol (a few hundred 2m bars a week) one page never
 * fills the view, and answering that re-ask with an empty "try later" left
 * the chart two-thirds blank until the user zoomed. Bounded so a truly
 * sparse symbol cannot chain fetches forever; past the cap the held ask is
 * answered empty with `more=true`, which keeps a later scroll working.
 */
const MAX_AUTO_FILL_PAGES = 6

/**
 * Bounded retry for a FAILED initial load (`loadHistory`). Attempt 0 is the
 * normal fetch; on a non-abort failure the load waits the matching backoff
 * and tries again, up to this many extra times, before emitting a single
 * `data:error`. Aborts and supersedes are never retried.
 */
const MAX_INITIAL_LOAD_RETRIES = 2
const INITIAL_LOAD_RETRY_BACKOFF_MS = [400, 1200] as const

/** Resolve after `ms`, or immediately once `signal` aborts (never rejects). */
function sleepUnlessAborted(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const done = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal.addEventListener('abort', done, { once: true })
  })
}

/**
 * Hard backstop: how many consecutive `Forward` callbacks may make zero
 * progress (deliver no new bars, no terminal state) before scroll-back stops
 * fetching entirely for the current `(symbol, period)`. The finer-grained
 * guards (`lastScrollBackLeftmost`, `MAX_SCROLLBACK_EMPTY_RETRIES`,
 * `MAX_PROBES`) already bound this in practice; this is the structural floor
 * that holds even if one of them regresses — "request storm via no-op
 * responses" can never exceed roughly `MAX_NO_PROGRESS_FORWARDS × (MAX_PROBES
 * + 1)` fetches. Reset per-(symbol, period).
 */
const MAX_NO_PROGRESS_FORWARDS = 5

/**
 * Defense-in-depth threshold. A scroll-back page wider than this that returns
 * ZERO bars while still strictly ABOVE the per-ticker floor is *suspicious*: a
 * ticker with a bar at the current leftmost almost never has 3+ empty weeks
 * immediately below it that aren't a holiday closure. The TSLA-30m chart gap
 * (backend month-range bug, fixed 2026-06) had exactly this shape — a wide
 * window erroneously returning empty — and the probe correctly walked past it,
 * leaving a silent gap.
 *
 * The frontend cannot safely *backfill* an arbitrary backend omission:
 * klinecharts prepends pages only, so correcting a mid-window hole would mean
 * re-deriving the backend's pagination and risks the very request-storm the
 * probe guards against. Skipping genuinely-absent data is the right behaviour.
 * So instead of correcting, we DETECT and surface the suspicious case via
 * `data:suspiciousEmptyWindow` + a `console.warn`, so a backend regression is
 * caught in dev/staging/Sentry instead of silently corrupting the chart. The
 * scroll-back stays retriable (the existing no-progress path keeps `more=true`)
 * so a healthy backend recovers on the next scroll.
 *
 * 21 days clears the longest market closures so normal holiday gaps don't
 * false-positive; a narrow empty page or one clamped to the real floor is
 * never flagged.
 */
const SUSPICIOUS_EMPTY_SPAN_MS = 21 * 24 * 60 * 60 * 1000

/**
 * Why scroll-back has run out of older bars. Drives `ChartManager`'s
 * first-bar history marker, auto-fit and pan-guard.
 *
 *   `'floor'`     — the per-ticker hard floor was reached (real thin history)
 *                   OR an empty fetch confirmed nothing older exists.
 *   `'lookback'`  — `LOOKBACK_BY_PERIOD`'s soft cap was reached before the
 *                   real floor; older data may still exist server-side.
 *   `null`        — neither limit reached (default state).
 *
 * Spec: docs/superpowers/specs/2026-05-07-chart-thin-history-ux-design.md
 */
export type ScrollBackExhaustedReason = 'floor' | 'lookback' | null

type LoadDataArgs = Parameters<Parameters<Chart['setLoadDataCallback']>[0]>[0]
type LoadDataCallback = LoadDataArgs['callback']

function toKLineData(bar: EdgeBar) {
  return {
    timestamp: bar.timestamp,
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    volume: bar.volume,
    turnover: bar.turnover,
  }
}

export class DataLayer {
  private chart: Chart | null = null
  private datafeed: DataFeed
  private eventBus: EventBus
  private currentSymbol: EdgeSymbol | null = null
  /** Monotonic id for loadHistory calls — see the staleness token inside it. */
  private loadHistoryToken = 0
  private currentPeriod: Period | null = null
  private currentCallback: ((bar: EdgeBar) => void) | null = null
  // The (symbol, period) the live callback was actually registered under. Used
  // to unsubscribe — NOT currentSymbol/currentPeriod, which setSymbol/setPeriod
  // drift to the NEW target before teardown, so unsubscribing by them looks up
  // the wrong datafeed key and leaks the old ticker's subscription (it keeps
  // feeding this chart the old ticker's bars).
  private subscribedSymbol: EdgeSymbol | null = null
  private subscribedPeriod: Period | null = null
  private loading = false
  /**
   * The load-data handler registered with klinecharts, kept so an ask that
   * arrived while a page was in flight can be serviced afterwards.
   */
  private loadDataHandler: ((args: LoadDataArgs) => unknown) | null = null
  /**
   * An older-data ask klinecharts made while `loading` was true. Held, not
   * answered, until the in-flight page settles; klinecharts' own `_loading`
   * stays true meanwhile, which is accurate. Only one is kept: a newer ask
   * supersedes an older one (the older is answered empty, `more=true`).
   */
  private pendingForward: { timestamp: number | null; callback: LoadDataCallback } | null = null
  private autoFillPages = 0
  /** True while `drainPendingForward` re-enters the handler on its own behalf. */
  private draining = false
  /**
   * Monotonically increasing generation counter, bumped on each `reload()`.
   * The scroll-back IIFE captures this at entry and re-checks after every
   * `await`; if the generation has changed (because reload happened), the
   * IIFE bails without writing to the chart or invoking its callback with
   * stale bars. Prevents the deadlock where a hanging scroll-back fetch
   * settles after `reload()` and contaminates the new chart state.
   */
  private scrollGeneration = 0
  private barFilter: ((bar: EdgeBar) => boolean) | null = null
  private anchorTimestamp: number | null = null
  /**
   * Extra reach (ms) the anchored initial fetch must cover so a multi-day
   * visible window has its prior-session bars in one request instead of several
   * scroll-back pages. 0 (default) ⇒ the fetch is sized purely by
   * `pageMsFromBars`, byte-identical to single-day / non-grid charts. Set by
   * `ChartManager` from the active window's day offset.
   */
  private minAnchorLookbackMs = 0
  /**
   * Extra reach (ms) the anchored fetch extends *past* the event day so an
   * outcome bound (D[2]+, "how the trade turned out") has its forward bars. 0
   * (default) ⇒ the fetch stops at the event day and the past-anchor trims cut
   * everything after it — the historical default, byte-identical for every
   * non-forward chart. Set by `ChartManager` from a forward end offset.
   */
  private anchorForwardMs = 0
  /**
   * Whether to include pre-/post-market bars. Threaded through to every
   * `datafeed.getHistoryBars` call so the implementation can request
   * `?market_session=mkt` (intraday only) when set to `false`. Defaults to
   * `true` (all-session). The barFilter is the symmetric client-side guard,
   * load-bearing for Ably realtime bars (which bypass the URL).
   */
  private extendedHours = true
  /** Exchange timezone from the last `setExtendedHours`; reads the 09:30 open. */
  private sessionTimezone: string | null = null
  /**
   * Regular-session predicate matching the active filter. Forwarded to the
   * datafeed so a regular-only history request can asymmetrically reuse a
   * cached all-session response by deriving (filter then return) instead of
   * refetching. `null` whenever extendedHours is true.
   */
  private regularSessionFilter: ((bar: EdgeBar) => boolean) | null = null
  /**
   * Whether to request split-adjusted (true, default) or unadjusted (false)
   * OHLCV. Threaded through to every `datafeed.getHistoryBars` call. The two
   * series have entirely different price values for any post-split bar — they
   * are NOT a filter relationship and the cache cannot cross-reuse them.
   */
  private adjusted = true
  /** Whether history fetches should ask for per-bar volume histograms. Off by
   *  default so charts without the volume-profile indicator build URLs (and
   *  therefore cache keys) identical to pre-feature ones. */
  private volumeProfile = false

  /**
   * Floor + scroll-back state per `(symbol, period)` (see chart-lookback spec
   * 2026-05-04, "Frontend — scroll-back loading in `DataLayer`").
   *
   * - `floor`: earliest-bar timestamp (ms) for the current symbol, resolved
   *   from `getFloor()` as `earliestDate ?? ipoDate ?? null`. Cached
   *   per-symbol so a `setPeriod()` does not re-hit the network.
   * - `floorTicker`: which ticker `floor` was fetched for; lets us avoid
   *   re-fetching when `setSymbol()` is called with the same ticker.
   * - `floorReached`: true once we've paged back to the floor or hit an
   *   empty response. Reset by both `setSymbol()` and `setPeriod()`.
   * - `scrollbackCapReached`: true once the in-memory bar count for the
   *   active `(ticker, period)` hits `MAX_BARS_PER_CHART`. Distinct from
   *   `floorReached` because older data still EXISTS — we just refuse to
   *   load it to bound memory. Reset by `setSymbol()` and `setPeriod()`.
   * - `loadingOlder`: true while a scroll-back fetch is in flight. C3's UI
   *   indicator subscribes to `data:loadingOlderChanged` to render a spinner.
   */
  private floor: number | null = null
  private floorTicker: string | null = null
  /** In-flight floor-lookup promise. Scroll-back awaits this so it never
   * fetches below the floor during the load window (race we saw in the wild
   * with FTNT 1m and VSEE 1M). Resolves once `this.floor` is populated. */
  private floorLoadPromise: Promise<void> | null = null
  private floorReached = false
  private scrollbackCapReached = false
  private loadingOlder = false
  private scrollBackExhaustedReason: ScrollBackExhaustedReason = null
  /**
   * Last leftmost the scroll-back callback handled. Dedup against klinecharts
   * re-firing forward for the same `data.timestamp` before its render loop has
   * updated the chart's leftmost from our previous response.
   *
   * Reset on setSymbol/setPeriod/reload. Also cleared by
   * `allowBoundedScrollBackRetry` (up to `MAX_SCROLLBACK_EMPTY_RETRIES` times
   * per leftmost) so a transient empty/errored fetch gets re-tried instead of
   * permanently freezing scroll-back. It is deliberately NOT cleared when a
   * page came back with bars that were all duplicates — that path keeps the
   * guard set so the backend re-serving already-loaded bars can't loop.
   */
  private lastScrollBackLeftmost: number | null = null
  /**
   * Bookkeeping for `allowBoundedScrollBackRetry`: which leftmost the running
   * retry budget belongs to, and how many empty/errored attempts have been
   * spent on it. Reset alongside `lastScrollBackLeftmost`.
   */
  private scrollBackEmptyRetryLeftmost: number | null = null
  private scrollBackEmptyRetryCount = 0
  /**
   * Backstop counters (see `MAX_NO_PROGRESS_FORWARDS`): how many `Forward`
   * callbacks in a row made zero progress, and a sticky flag set once the
   * limit is hit — thereafter every `Forward` is answered `[], false` so
   * klinecharts stops asking. Both reset per-(symbol, period).
   */
  private noProgressForwards = 0
  private scrollBackStuck = false
  /**
   * AbortController for the in-flight scroll-back fetch chain (one per
   * `Forward` callback invocation). `reload`/`setSymbol`/`setPeriod` abort it
   * so a view change cancels the now-irrelevant network calls instead of just
   * discarding their results via the `scrollGeneration` check.
   */
  private scrollBackAbort: AbortController | null = null
  /**
   * AbortController for the in-flight INITIAL load (`loadHistory`). Aborted by
   * `destroy` (a history-grid cell that unmounts mid-load, e.g. the user paged
   * on, must not leave its chart-series request running on the server) and
   * by the next `loadHistory` (the superseded request was only ever going to
   * be dropped by the staleness token; cancelling it saves the round-trip).
   */
  private loadHistoryAbort: AbortController | null = null

  constructor(datafeed: DataFeed, eventBus: EventBus) {
    this.datafeed = datafeed
    this.eventBus = eventBus
  }

  setChart(chart: Chart): void {
    this.chart = chart
    this.setupLoadDataCallback()
  }

  setDatafeed(datafeed: DataFeed): void {
    this.unsubscribe()
    this.datafeed = datafeed
  }

  setBarFilter(filter: ((bar: EdgeBar) => boolean) | null): void {
    this.barFilter = filter
  }

  /**
   * Apply the active client-side bar filter, gated on the current period
   * being intraday. The filter today is `createRegularSessionFilter`, which
   * checks whether a timestamp falls in 09:30–16:00 ET — daily/weekly/monthly
   * bars sit at session-aligned timestamps OUTSIDE that window (typically
   * midnight ET) and would be rejected wholesale, leaving the chart blank.
   *
   * Mirrors the URL gating in `EdgeDataFeed`: `&market_session=mkt` is sent
   * only on intraday URLs for the same reason. Keeping both gates aligned
   * means client-side and server-side filtering have the same semantic.
   *
   * Realtime bars route through this same helper via the `subscribe` callback
   * (per-bar variant). Daily realtime is rare in practice but the gating is
   * symmetric.
   */
  private applyBarFilter(bars: EdgeBar[]): EdgeBar[] {
    if (!this.barFilter) return bars
    if (!this.currentPeriod) return bars
    const isIntraday = isIntradayPeriod(this.currentPeriod)
    if (!isIntraday) return bars
    return bars.filter(this.barFilter)
  }

  /**
   * Daily candles are exclusive to regular market hours: before the 09:30 ET
   * open the feed already serves a "today" bar built from premarket trades
   * alone. Drop that forming bar from initial loads so the chart shows no
   * candle for today yet, and surface its close through 'premarket:price' so
   * the y-axis keeps a live price indication. Emits a `price: null` clear
   * whenever the gate is inactive (intraday period, market open, anchored
   * history) so the marker never outlives its reason.
   *
   * Only called on the two initial-load paths — scroll-back pages contain
   * strictly older bars and must not touch the marker.
   */
  private suppressFormingDailyBar(bars: EdgeBar[]): EdgeBar[] {
    if (this.currentPeriod?.timespan !== 'day' || this.anchorTimestamp != null) {
      this.emitPremarketPrice(null)
      return bars
    }
    const last = bars[bars.length - 1]
    if (!last || !isPremarketFormingDailyBar(last.timestamp, Date.now())) {
      this.emitPremarketPrice(null)
      return bars
    }
    this.emitPremarketPrice(last.close, last.timestamp)
    return bars.slice(0, -1)
  }

  private premarketMarkerShown = false

  private emitPremarketPrice(price: number | null, timestamp?: number): void {
    if (price == null) {
      if (!this.premarketMarkerShown) return
      this.premarketMarkerShown = false
      this.eventBus.emit('premarket:price', { price: null })
      return
    }
    this.premarketMarkerShown = true
    this.eventBus.emit('premarket:price', { price, timestamp })
  }

  /** Per-bar variant of `applyBarFilter` for the realtime subscribe callback. */
  private shouldKeepRealtimeBar(bar: EdgeBar): boolean {
    if (!this.barFilter) return true
    if (!this.currentPeriod) return true
    const isIntraday = isIntradayPeriod(this.currentPeriod)
    if (!isIntraday) return true
    return this.barFilter(bar)
  }

  /**
   * Configure whether subsequent fetches should include pre-/post-market bars.
   * Mirrors the user-facing toggle in SettingsModal:
   *
   * - `enabled=true` clears the client-side filter and lets the datafeed default
   *   to `?market_session=all` (or omitting the param entirely on daily+).
   * - `enabled=false` installs the regular-session filter and threads
   *   `extendedHours=false` plus the predicate into every `getHistoryBars`
   *   call. The datafeed appends `&market_session=mkt` for intraday URLs and
   *   uses the predicate for asymmetric cache reuse (derive mkt from a cached
   *   all-session response instead of refetching).
   *
   * The barFilter is still load-bearing for Ably realtime bars — those bypass
   * the URL and arrive over a separate channel — so this method updates both
   * the boolean and the filter in lockstep.
   */
  setExtendedHours(enabled: boolean, timezone: string): void {
    this.extendedHours = enabled
    this.sessionTimezone = timezone
    if (enabled) {
      this.barFilter = null
      this.regularSessionFilter = null
    } else {
      const filter = createRegularSessionFilter(timezone)
      this.barFilter = filter
      this.regularSessionFilter = filter
    }
  }

  /**
   * The 09:30-open bucketing for a regular-session-only chart, or null for
   * clock buckets. Realtime bars and replay candles use it so they land on
   * the server's `market_session=mkt` bars (1H at 09:30, 10:30 ... 15:30).
   */
  regularSessionBuckets(): RegularSessionBuckets | null {
    if (this.extendedHours || !this.sessionTimezone) return null
    return { timezone: this.sessionTimezone }
  }

  /**
   * Configure whether subsequent fetches should request split-adjusted (true)
   * or unadjusted (false) OHLCV. Mirrors the user-facing toggle in
   * SettingsModal. The flag flows through to `datafeed.getHistoryBars` opts;
   * the datafeed decides how to encode it on the URL (today: append
   * `&adjusted=false` only when explicitly off).
   *
   * Adjusted vs. unadjusted prices differ on every post-split bar — there is
   * no filter relationship between the two and the cache treats them as
   * independent entries. The caller is expected to follow this with a reload
   * (ChartManager does so).
   */
  setAdjusted(adjusted: boolean): void {
    this.adjusted = adjusted
  }

  /** Read the current adjusted flag — used by ChartManager.prefetch to thread
   *  the same mode into cache keys + URL params as the foreground load. */
  isAdjusted(): boolean {
    return this.adjusted
  }

  /**
   * Configure whether subsequent fetches request per-bar volume-price
   * histograms (`vp`), letting the volume profile bin at 1-minute fidelity
   * instead of smearing each bar across its own range.
   *
   * Unlike `adjusted`, this IS a pure superset relationship — a histogram-
   * bearing response contains everything the plain one does. But it still
   * changes the URL and therefore the cache key, so the caller is expected to
   * follow this with a reload (ChartManager does so), exactly as with
   * `setAdjusted`.
   */
  setVolumeProfile(enabled: boolean): void {
    this.volumeProfile = enabled
  }

  /** Read the current volume-profile flag — used by ChartManager.prefetch to
   *  thread the same mode into cache keys + URL params as the foreground load. */
  isVolumeProfile(): boolean {
    return this.volumeProfile
  }

  /**
   * Build the per-call options forwarded to `datafeed.getHistoryBars`. Pulled
   * out so `loadHistory` and the scroll-back callback emit the exact same
   * shape, ensuring URLs (and therefore caches) stay aligned.
   */
  private buildHistoryBarsOpts(extra: { signal?: AbortSignal } = {}): {
    signal?: AbortSignal
    extendedHours: boolean
    regularSessionFilter?: (bar: EdgeBar) => boolean
    adjusted: boolean
    volumeProfile: boolean
  } {
    const opts: {
      signal?: AbortSignal
      extendedHours: boolean
      regularSessionFilter?: (bar: EdgeBar) => boolean
      adjusted: boolean
      volumeProfile: boolean
    } = {
      ...extra,
      extendedHours: this.extendedHours,
      adjusted: this.adjusted,
      volumeProfile: this.volumeProfile,
    }
    if (this.regularSessionFilter) {
      opts.regularSessionFilter = this.regularSessionFilter
    }
    return opts
  }

  setAnchorTimestamp(ts: number | null): void {
    this.anchorTimestamp = ts
  }

  getAnchorTimestamp(): number | null {
    return this.anchorTimestamp
  }

  /**
   * Set the extra reach the anchored initial fetch must cover (see
   * {@link minAnchorLookbackMs}). 0 restores the plain page-sized fetch.
   */
  setMinAnchorLookbackMs(ms: number): void {
    this.minAnchorLookbackMs = Math.max(0, ms)
  }

  /**
   * Set how far past the event day the anchored fetch extends (see
   * {@link anchorForwardMs}). 0 restores the event-day-pinned historical fetch.
   */
  setAnchorForwardMs(ms: number): void {
    this.anchorForwardMs = Math.max(0, ms)
  }

  /**
   * The resolved earliest-bar floor for the current symbol (ms), or null if not
   * yet known. Lets a caller skip a reload-for-more that would reach below the
   * data that exists.
   */
  getFloorTimestamp(): number | null {
    return this.floor
  }

  /**
   * Resolve and cache the per-symbol floor timestamp. Called once per ticker;
   * subsequent calls with the same ticker are a cheap no-op (reuses cache).
   *
   * Resets the per-period scroll-back flags so a new symbol starts in the
   * `idle` state regardless of where the previous symbol left off.
   *
   * @param symbol The new symbol. The floor lookup keys off `symbol.ticker`.
   */
  /**
   * Reset the per-period scroll-back state machine. Bumps `scrollGeneration`
   * FIRST so any in-flight scroll-back IIFE sees a stale generation at its next
   * await checkpoint and bails without writing chart state; then clears every
   * flag/counter and aborts the in-flight fetch. Single source of truth shared
   * by setSymbol / setPeriod / reload so a newly-added field can't be forgotten
   * at one of the three call sites.
   *
   * NOTE: `setSymbol` additionally clears `floorLoadPromise` (a cross-ticker
   * concern, not per-period), which is intentionally NOT reset here.
   */
  private resetScrollbackState(): void {
    this.scrollGeneration += 1
    this.floorReached = false
    this.scrollbackCapReached = false
    this.loading = false
    this.setLoadingOlder(false)
    this.setScrollBackExhaustedReason(null)
    this.lastScrollBackLeftmost = null
    this.scrollBackEmptyRetryLeftmost = null
    this.scrollBackEmptyRetryCount = 0
    this.noProgressForwards = 0
    this.scrollBackStuck = false
    this.scrollBackAbort?.abort()
    // Never strand a held ask: klinecharts' `_loading` stays true until it is
    // answered, and a symbol/period switch must leave the new chart free.
    const pending = this.pendingForward
    this.pendingForward = null
    pending?.callback([], true)
    this.autoFillPages = 0
  }

  async setSymbol(symbol: EdgeSymbol): Promise<void> {
    // Reset the scroll-back state machine for the new symbol (bumps generation
    // so in-flight IIFEs bail), then clear the cross-ticker floor-load promise.
    this.resetScrollbackState()
    this.floorLoadPromise = null
    // Track the active symbol so the scroll-back callback knows what to fetch
    // even before `subscribe()` is wired (e.g. anchored / focus-timestamp
    // mode, which never subscribes for live updates).
    this.currentSymbol = symbol

    if (this.floorTicker === symbol.ticker) {
      // Already fetched for this ticker — keep the cached floor.
      return
    }
    this.floorTicker = symbol.ticker
    this.floor = null

    if (!hasGetFloor(this.datafeed)) {
      // Datafeed has no floor capability; fall back to scroll-until-empty.
      this.floorLoadPromise = null
      return
    }
    // Capture the in-flight load promise so scroll-back can await it before
    // computing the per-ticker clamp (race we saw with FTNT 1m and VSEE 1M
    // where a fast scroll fired its fetch with `this.floor = null` and the
    // clamp didn't see the per-ticker floor).
    //
    // Null the promise once it settles so the scroll-back's `await` is a
    // no-op when floor is already loaded — avoids an unnecessary microtask
    // yield in the hot path.
    const ticker = symbol.ticker
    const floorCapable = this.datafeed
    const load = (async () => {
      try {
        const payload = await floorCapable.getFloor(ticker)
        if (this.floorTicker === ticker) {
          this.floor = resolveFloorMs(payload)
        }
      } catch {
        if (this.floorTicker === ticker) this.floor = null
      }
    })()
    this.floorLoadPromise = load
    try {
      await load
    } finally {
      if (this.floorLoadPromise === load) this.floorLoadPromise = null
    }
  }

  /**
   * Note a period switch. Resets the per-period state machine
   * (`floorReached`, `loadingOlder`) without touching the cached floor.
   *
   * Call sites: `ChartManager.setPeriod()` should call this before invoking
   * `loadHistory()` so the next scroll-back attempt re-runs from `idle`.
   */
  setPeriod(period: Period): void {
    // Reset the scroll-back state machine — any IIFE started for the previous
    // period must abandon mid-flight (the generation bump makes it bail).
    this.resetScrollbackState()
    this.currentPeriod = period
  }

  /** Current cached floor in ms, or null if unknown. */
  getFloor(): number | null {
    return this.floor
  }

  /** True once we've paged back to the floor or hit an empty response. */
  isFloorReached(): boolean {
    return this.floorReached
  }

  /**
   * True once the in-memory bar count for the active `(ticker, period)` hit
   * `MAX_BARS_PER_CHART` and further scroll-back fetches have been suppressed.
   * Reset by `setSymbol()` / `setPeriod()` (both replace the chart buffer
   * wholesale on their next load).
   */
  isScrollbackCapReached(): boolean {
    return this.scrollbackCapReached
  }

  /** True while a scroll-back fetch is in flight. */
  isLoadingOlder(): boolean {
    return this.loadingOlder
  }

  private setLoadingOlder(value: boolean): void {
    if (this.loadingOlder === value) return
    this.loadingOlder = value
    this.eventBus.emit('data:loadingOlderChanged', { loading: value })
  }

  /** Current reason scroll-back is exhausted, or null if not exhausted. */
  getScrollBackExhaustedReason(): ScrollBackExhaustedReason {
    return this.scrollBackExhaustedReason
  }

  /**
   * Reject any bar whose timestamp is strictly before the per-ticker floor.
   * The floor (`etl_latest_dates.earliest_date`, parsed at midnight UTC) is
   * INCLUSIVE — bars at exactly the floor timestamp are kept (e.g. IPO-day
   * bars when floor = IPO date). Bars BEFORE the floor are dangling/bad data
   * and must never reach klinecharts.
   *
   * No-op when the floor is unknown.
   */
  private filterBelowFloor(bars: EdgeBar[], ticker: string, periodLabel: string): EdgeBar[] {
    if (this.floor == null || bars.length === 0) return bars
    const floor = this.floor
    const out: EdgeBar[] = []
    let droppedCount = 0
    let earliestDropped = Number.POSITIVE_INFINITY
    for (const bar of bars) {
      if (bar.timestamp < floor) {
        droppedCount++
        if (bar.timestamp < earliestDropped) earliestDropped = bar.timestamp
      } else {
        out.push(bar)
      }
    }
    if (droppedCount > 0) {
      console.warn(`[chart] floor cutoff dropped ${droppedCount} bar(s) before floor`, {
        ticker,
        period: periodLabel,
        droppedCount,
        floorTimestamp: floor,
        earliestDroppedTimestamp: earliestDropped,
      })
      this.eventBus.emit('data:belowFloorBarsDropped', {
        ticker,
        periodLabel,
        droppedCount,
        floorTimestamp: floor,
        earliestDroppedTimestamp: earliestDropped,
      })
    }
    return out
  }

  /**
   * Drop bars whose timestamps already exist in klinecharts' buffer. Should be
   * a no-op in normal flow; a hit signals an upstream regression and emits a
   * `console.warn` (auto-captured by Sentry once integrated) plus a
   * `data:duplicateBarsDropped` event for the debug panel.
   */
  private dedupAgainstChart(bars: EdgeBar[], ticker: string, periodLabel: string): EdgeBar[] {
    if (!this.chart || bars.length === 0) return bars
    const existing = new Set<number>()
    const dataList = this.chart.getDataList() as unknown as readonly EdgeBar[]
    for (const bar of dataList) existing.add(bar.timestamp)
    const out: EdgeBar[] = []
    const droppedTimestamps: number[] = []
    for (const bar of bars) {
      if (existing.has(bar.timestamp)) droppedTimestamps.push(bar.timestamp)
      else out.push(bar)
    }
    if (droppedTimestamps.length > 0) {
      console.warn(`[chart] dedup guard dropped ${droppedTimestamps.length} duplicate bar(s)`, {
        ticker,
        period: periodLabel,
        droppedCount: droppedTimestamps.length,
        sampleTimestamps: droppedTimestamps.slice(0, 3),
      })
      this.eventBus.emit('data:duplicateBarsDropped', {
        ticker,
        periodLabel,
        droppedCount: droppedTimestamps.length,
        sampleTimestamps: droppedTimestamps.slice(0, 3),
      })
    }
    return out
  }

  /**
   * Mutate the exhaustion reason and emit `scrollback:exhausted` on transition.
   * Setting the same value twice is a no-op (event-on-transition).
   */
  private setScrollBackExhaustedReason(reason: ScrollBackExhaustedReason): void {
    if (this.scrollBackExhaustedReason === reason) return
    this.scrollBackExhaustedReason = reason
    this.eventBus.emit('scrollback:exhausted', { reason })
  }

  /**
   * A scroll-back attempt for `leftmost` produced nothing usable (a thrown
   * fetch, a transient empty response, or a genuine multi-day gap above the
   * floor) and delivered no bars — so klinecharts' next `Forward` fire will
   * carry the same `leftmost` and the `lastScrollBackLeftmost` guard would
   * suppress it. Clear that guard so the re-fire retries — but only up to
   * `MAX_SCROLLBACK_EMPTY_RETRIES` times for the same `leftmost`, after which
   * we leave the guard set so a persistent failure can't become a tight loop.
   */
  private allowBoundedScrollBackRetry(leftmost: number): void {
    if (this.scrollBackEmptyRetryLeftmost === leftmost) {
      this.scrollBackEmptyRetryCount += 1
    } else {
      this.scrollBackEmptyRetryLeftmost = leftmost
      this.scrollBackEmptyRetryCount = 1
    }
    if (this.scrollBackEmptyRetryCount <= MAX_SCROLLBACK_EMPTY_RETRIES) {
      this.lastScrollBackLeftmost = null
    }
  }

  private setupLoadDataCallback(): void {
    if (!this.chart) return

    const handler = ({ type, data, callback }: LoadDataArgs) => {
      if (type === LOAD_DATA_TYPE.Init) {
        // Initial load is handled by loadHistory() + applyNewData().
        // Acknowledge the callback so klinecharts enables Forward requests.
        callback([], true)
        return
      }
      if (type !== LOAD_DATA_TYPE.Forward) {
        callback([], false)
        return
      }

      // Always invoke callback when bailing — klinecharts sets _loading=true
      // before calling us; skipping callback strands _loading=true forever.
      // This branch is reachable synchronously from inside our own
      // `callback(bars, true)` delivery: klinecharts' addData prepends, then
      // runs adjustVisibleRange, and when the view is still short after the
      // prepend (zoomed out, or a thin symbol whose page holds fewer bars
      // than the viewport) the visible `from` is still 0, so it re-fires
      // Forward while `loading` hasn't been cleared yet. That ask is held
      // and serviced with the NEXT page once this one settles (see
      // `drainPendingForward`), so a thin symbol fills the viewport without a
      // zoom. Whatever answers it must keep `more=true`: `more=false` sets
      // klinecharts `_forwardMore=false` permanently and older-data loading
      // silently dies (the "daily chart walls at ~2022" bug). An empty
      // Forward delivery skips adjustVisibleRange in klinecharts, so the
      // capped answer cannot re-fire and there is no loop.
      if (this.loading) {
        this.deferForward(data?.timestamp ?? null, callback)
        return
      }
      // A genuine user scroll (not our own drain) starts a fresh auto-fill
      // budget; the cap bounds one chain, not the whole session.
      if (!this.draining) this.autoFillPages = 0
      if (this.floorReached) {
        // We've paged back to the per-ticker floor (or hit an empty response).
        // No more older data exists; tell klinecharts so it stops asking.
        callback([], false)
        return
      }
      if (this.scrollbackCapReached) {
        // Soft memory cap hit on a previous scroll-back step. Older data
        // does exist, but we refuse to load more until the user resets the
        // chart (period / ticker switch). `more=false` silences klinecharts.
        callback([], false)
        return
      }
      if (this.scrollBackStuck) {
        // Backstop tripped: too many consecutive no-progress fires. Stop.
        callback([], false)
        return
      }
      if (!this.currentSymbol || !this.currentPeriod) {
        callback([], true) // more=true: data exists, we just can't fetch yet
        return
      }
      if (!this.chart) {
        callback([], false)
        return
      }
      // Cheap pre-check — bail before issuing the network request when the
      // chart buffer is already at the cap. The fetch path also re-checks
      // post-prepend in case a single page would push us over.
      if (this.chart.getDataList().length >= MAX_BARS_PER_CHART) {
        this.scrollbackCapReached = true
        callback([], false)
        return
      }

      const timestamp = data?.timestamp
      if (timestamp == null) {
        callback([], false)
        return
      }

      // klinecharts can fire forward several times in succession during fast
      // scrolling — chart's leftmost hasn't updated from our previous response
      // yet, so each fire passes the same `data.timestamp`. URL-level inflight
      // dedup doesn't help once the previous fetch has settled.
      if (this.lastScrollBackLeftmost === timestamp) {
        callback([], false)
        return
      }

      // `to = leftmost − period.ms` (not −1ms): `EdgeDataFeed.ceilToBarBoundary`
      // rounds `to` UP, so −1ms would round back to `leftmost` and the server
      // would return the leftmost bar again, causing duplicates.
      const pageMs = pageMsFromBars(this.currentPeriod)
      const periodMs = this.currentPeriod.ms
      const initialTo = timestamp - periodMs
      const rawInitialFrom = timestamp - pageMs

      const periodKey = periodKeyOf(this.currentPeriod)
      const lookbackCap = periodKey != null ? LOOKBACK_BY_PERIOD[periodKey] : null
      const lookbackFloor =
        lookbackCap != null && Number.isFinite(lookbackCap) ? Date.now() - lookbackCap : null

      this.loading = true
      this.setLoadingOlder(true)
      this.lastScrollBackLeftmost = timestamp
      // Capture the generation at IIFE entry — checked after every await
      // inside the async block to bail if `reload()` ran in the meantime.
      const myGen = this.scrollGeneration
      // One AbortController per scroll-back run; `reload`/`setSymbol`/`setPeriod`
      // abort it so a view change cancels these fetches mid-flight.
      const abort = new AbortController()
      this.scrollBackAbort = abort

      // Sparse-probe: walk backward through weekend/holiday gaps automatically
      // instead of stranding the user at a sparse leftmost where klinecharts
      // can't re-fire forward. Absolute threshold — sparse weekend responses
      // are < 10 bars regardless of period. MAX_PROBES of 3 + initial = 4
      // fetches covering ~6.8 days back at 1.7d/page.
      const SPARSE_THRESHOLD = 10
      const MAX_PROBES = 3

      const ds = this.datafeed
      const sym = this.currentSymbol
      const per = this.currentPeriod

      const fetchPage = async (
        fetchFrom: number,
        fetchTo: number,
        fromClamped: boolean,
      ): Promise<{ bars: EdgeBar[]; hitFloor: boolean }> => {
        const bars = await ds.getHistoryBars(
          sym,
          per,
          fetchFrom,
          fetchTo,
          this.buildHistoryBarsOpts({ signal: abort.signal }),
        )
        // Empty + clamped = real exhaustion. Empty + unclamped = market-closed
        // gap above the floor; older data may still exist below.
        const hitFloor = bars.length === 0 && fromClamped
        return { bars, hitFloor }
      }

      return (async () => {
        // Wait for any in-flight floor lookup so the per-ticker clamp below
        // sees the correct floor (the race that caused FTNT-style fetches
        // below the actual valid range).
        if (this.floorLoadPromise) await this.floorLoadPromise
        // Bail if `reload()` ran while we awaited — the chart state has been
        // cleared and re-loaded under a new generation; our results are stale.
        if (myGen !== this.scrollGeneration) return

        const effectiveFloor =
          this.floor != null && lookbackFloor != null
            ? Math.max(this.floor, lookbackFloor)
            : (this.floor ?? lookbackFloor)

        // Real-floor wins on tie: same date in both means we have everything.
        let exhaustionReason: ScrollBackExhaustedReason
        if (this.floor != null && lookbackFloor != null) {
          exhaustionReason = this.floor >= lookbackFloor ? 'floor' : 'lookback'
        } else if (this.floor != null) {
          exhaustionReason = 'floor'
        } else {
          exhaustionReason = 'lookback'
        }

        const initialFromClamped = effectiveFloor != null && rawInitialFrom < effectiveFloor
        const initialFrom = initialFromClamped ? (effectiveFloor as number) : rawInitialFrom

        if (initialFrom >= initialTo + 1) {
          // Clamped range is empty — we are at (or past) the floor.
          if (this.chart) {
            this.floorReached = true
            this.setScrollBackExhaustedReason(exhaustionReason)
          }
          callback([], false)
          return
        }

        // `allNewBars` accumulates only bars the chart does NOT already hold:
        // each page is reduced (floor cutoff → session filter → dedup against
        // the chart buffer) BEFORE being added. A page that reduces to nothing
        // — because the backend re-served bars already on screen (e.g. the
        // QuoteMedia "current-day" injection riding along on a historical-range
        // request) — is treated exactly like an empty page: we walk further
        // back rather than handing klinecharts an empty result for the same
        // window over and over. This is what stops the scroll-back retry storm.
        let allNewBars: EdgeBar[] = []
        // Timestamps already accepted into `allNewBars` this callback —
        // `dedupAgainstChart` only checks klinecharts' buffer, which isn't
        // updated mid-callback, so a backend that returns overlapping/superset
        // pages across probes would otherwise hand klinecharts duplicate
        // timestamps. Dedup each page against this set too.
        const acceptedTimestamps = new Set<number>()
        let nextTo = initialTo
        let nextRawFrom = rawInitialFrom
        let nextFromClamped = initialFromClamped
        let nextFrom = initialFrom
        let exhausted = false
        let probesUsed = 0
        // Defense-in-depth: the first wide, above-floor window that came back
        // raw-empty this callback. Surfaced (not corrected) after the loop —
        // see SUSPICIOUS_EMPTY_SPAN_MS.
        let suspiciousEmpty: { fromMs: number; toMs: number } | null = null
        // True once any probe returned a non-empty raw response. Distinguishes
        // "the backend re-served bars we already hold" (keep the re-fire guard:
        // a retry just reproduces the storm) from "we found nothing at all"
        // (a transient empty/errored fetch or a genuine gap — allow a bounded
        // retry, see `allowBoundedScrollBackRetry`).
        let sawAnyRawBars = false

        while (probesUsed <= MAX_PROBES) {
          const tryFrom = nextFrom
          const { bars, hitFloor } = await fetchPage(tryFrom, nextTo, nextFromClamped)
          // After every fetch await, re-check the generation. A reload during
          // a hanging probe must abandon the in-flight chain immediately.
          if (myGen !== this.scrollGeneration) return
          if (hitFloor) {
            exhausted = true
            break
          }
          if (!this.chart) return
          if (bars.length > 0) sawAnyRawBars = true

          // Flag the first wide window that returned ZERO raw bars while still
          // above the real per-ticker floor (NOT merely clamped to the soft
          // lookback cap — data may well exist below that). Detection only; the
          // walk is unchanged. See SUSPICIOUS_EMPTY_SPAN_MS.
          if (
            !suspiciousEmpty &&
            bars.length === 0 &&
            nextTo - tryFrom > SUSPICIOUS_EMPTY_SPAN_MS &&
            (this.floor == null || tryFrom > this.floor)
          ) {
            suspiciousEmpty = { fromMs: tryFrom, toMs: nextTo }
          }

          // Reduce this page to the bars genuinely older than what's loaded:
          // floor cutoff (drops dangling/bad pre-floor data), session filter,
          // dedup against klinecharts' buffer, then dedup against bars already
          // accepted earlier in this callback (overlapping backend pages).
          let pageNew = this.filterBelowFloor(bars, sym.ticker, per.label)
          pageNew = this.applyBarFilter(pageNew)
          pageNew = this.dedupAgainstChart(pageNew, sym.ticker, per.label)
          if (acceptedTimestamps.size > 0 && pageNew.length > 0) {
            const before = pageNew.length
            pageNew = pageNew.filter((b) => !acceptedTimestamps.has(b.timestamp))
            if (pageNew.length < before) {
              console.warn(
                `[chart] scroll-back dropped ${before - pageNew.length} bar(s) already accepted in an earlier probe (overlapping backend pages)`,
                { ticker: sym.ticker, period: per.label },
              )
            }
          }

          if (pageNew.length > 0) {
            for (const b of pageNew) acceptedTimestamps.add(b.timestamp)
            allNewBars = [...pageNew, ...allNewBars]
            if (pageNew.length >= SPARSE_THRESHOLD) break
            // Walk back from the oldest NEW bar. `−periodMs` (not −1ms) keeps
            // ceilToBarBoundary from rounding `to` back to `newLeftmost`.
            const newLeftmost = pageNew[0].timestamp
            nextTo = newLeftmost - periodMs
            nextRawFrom = newLeftmost - pageMs
          } else {
            // Empty (or all-duplicate) page — walk past the just-tried window
            // so the next probe doesn't refetch it.
            nextTo = tryFrom - periodMs
            nextRawFrom = tryFrom - pageMs
          }

          nextFromClamped = effectiveFloor != null && nextRawFrom < effectiveFloor
          nextFrom = nextFromClamped ? (effectiveFloor as number) : nextRawFrom
          if (nextFrom >= nextTo + 1) {
            // Clamp produced empty range — we're at the floor.
            exhausted = true
            break
          }
          probesUsed += 1
        }

        if (!this.chart) return
        // Final generation check before writing chart state.
        if (myGen !== this.scrollGeneration) return
        const newCount = this.chart.getDataList().length + allNewBars.length
        if (allNewBars.length > 0 && newCount >= MAX_BARS_PER_CHART) {
          this.scrollbackCapReached = true
        }
        // `more=false` for genuine terminal states (exhaustion / memory cap).
        // Otherwise keep `more=true` so a later user scroll can still try.
        let stillMore: boolean
        if (exhausted || newCount >= MAX_BARS_PER_CHART) {
          stillMore = false
        } else {
          stillMore = true
          if (allNewBars.length > 0) {
            // Real progress — the chart's leftmost advanced. Clear the backstop.
            this.noProgressForwards = 0
          } else {
            if (!sawAnyRawBars) {
              // No bars at all across every probe — a transient empty/errored
              // fetch or a real multi-day gap above the floor. Allow klinecharts
              // a bounded number of retries for this leftmost. (When the pages
              // came back WITH bars that were all duplicates — `sawAnyRawBars` —
              // we deliberately leave the guard set: re-fetching would only
              // re-receive the same already-loaded bars, i.e. the storm.)
              this.allowBoundedScrollBackRetry(timestamp)
            }
            // Backstop: too many consecutive zero-progress fires → stop fetching
            // regardless of what the finer-grained guards above decided.
            this.noProgressForwards += 1
            if (this.noProgressForwards >= MAX_NO_PROGRESS_FORWARDS) {
              this.scrollBackStuck = true
              stillMore = false
            }
          }
        }
        if (exhausted) {
          this.floorReached = true
          this.setScrollBackExhaustedReason(exhaustionReason)
        }
        // Surface a suspicious wide-empty window (a likely backend data gap) so
        // a regression is loud, not silent. Detection only — we do NOT alter
        // `stillMore`, so scroll-back stays retriable and a healthy backend
        // recovers on the next scroll. Suppressed once we've genuinely reached
        // the floor (that's real exhaustion, not a suspicious omission).
        if (suspiciousEmpty && !exhausted) {
          console.warn(
            '[chart] scroll-back: a wide window returned no bars while above the ' +
              'ticker floor — possible backend data gap. The data layer cannot ' +
              'backfill this (klinecharts prepends only); surfacing for visibility.',
            {
              ticker: sym.ticker,
              period: per.label,
              fromMs: suspiciousEmpty.fromMs,
              toMs: suspiciousEmpty.toMs,
            },
          )
          this.eventBus.emit('data:suspiciousEmptyWindow', {
            ticker: sym.ticker,
            periodLabel: per.label,
            fromMs: suspiciousEmpty.fromMs,
            toMs: suspiciousEmpty.toMs,
          })
        }
        callback(allNewBars.map(toKLineData), stillMore)
        if (allNewBars.length > 0) {
          this.eventBus.emit('data:loaded', { bars: allNewBars, symbol: sym.ticker })
        }
      })()
        .catch((err) => {
          // Stale generation: the chart was reloaded while we were in flight.
          // Don't write to the chart, don't surface error toasts.
          if (myGen !== this.scrollGeneration) return
          // The fetch threw (transient network error, abort, etc.) and no bars
          // were delivered, so klinecharts will re-fire `Forward` with the same
          // leftmost. Allow a bounded retry rather than freezing scroll-back at
          // this spot forever; a persistent failure stops after the budget.
          this.allowBoundedScrollBackRetry(timestamp)
          callback([], true)
          this.eventBus.emit('data:error', {
            error: err instanceof Error ? err : new Error(String(err)),
            context: 'loadData',
          })
        })
        .finally(() => {
          if (this.scrollBackAbort === abort) this.scrollBackAbort = null
          // Only clear `loading` if WE are the active generation. A stale
          // IIFE that finishes after `reload()` must not flip the flag from
          // true → false because the new IIFE has set it to true for itself.
          if (myGen !== this.scrollGeneration) return
          this.loading = false
          this.setLoadingOlder(false)
          this.drainPendingForward()
        })
    }
    this.loadDataHandler = handler
    this.chart.setLoadDataCallback(handler)
  }

  /** Hold an older-data ask that arrived mid-fetch; see `pendingForward`. */
  private deferForward(timestamp: number | null, callback: LoadDataCallback): void {
    this.pendingForward?.callback([], true)
    this.pendingForward = { timestamp, callback }
  }

  /**
   * Service the ask held by `deferForward` now that no page is in flight.
   * Re-enters the normal scroll-back path with klinecharts' own callback, so
   * the page is delivered exactly as a user-initiated one would be. Stops at
   * `MAX_AUTO_FILL_PAGES` per symbol/period, or once scroll-back has declared
   * itself exhausted, answering empty so klinecharts stays willing to ask
   * again on the next scroll.
   */
  private drainPendingForward(): void {
    const pending = this.pendingForward
    this.pendingForward = null
    if (!pending) return
    const exhausted = this.floorReached || this.scrollbackCapReached || this.scrollBackStuck
    if (
      exhausted ||
      pending.timestamp == null ||
      !this.chart ||
      !this.loadDataHandler ||
      this.autoFillPages >= MAX_AUTO_FILL_PAGES
    ) {
      pending.callback([], !exhausted)
      return
    }
    this.autoFillPages += 1
    this.draining = true
    try {
      this.loadDataHandler({
        type: LOAD_DATA_TYPE.Forward,
        data: { timestamp: pending.timestamp, open: 0, high: 0, low: 0, close: 0 },
        callback: pending.callback,
      })
    } finally {
      this.draining = false
    }
  }

  /**
   * Apply pre-loaded bars to the chart, bypassing the network fetch. Used by
   * `ChartManager` when `setPeriod()` hits the multi-period preload cache —
   * the bars are already in memory, we just need to filter + render them and
   * emit `data:loaded` so dependents (axis recalc, debug panel) see the same
   * shape they would have on a normal load.
   *
   * The same `barFilter` and anchor-trim logic as `loadHistory()` apply, so
   * cache-hit and miss paths produce identical chart state.
   *
   * Spec: docs/superpowers/specs/2026-05-06-chart-multi-period-preload-design.md
   */
  applyPreloadedBars(symbol: EdgeSymbol, period: Period, bars: EdgeBar[]): void {
    if (!this.chart) return
    this.currentSymbol = symbol
    this.currentPeriod = period

    // Hard cutoff first: anything before the floor is dangling/bad data.
    let filtered = this.filterBelowFloor(bars, symbol.ticker, period.label)
    filtered = this.applyBarFilter(filtered)
    filtered = this.suppressFormingDailyBar(filtered)
    if (this.anchorTimestamp != null) {
      const cutoff = this.anchorTimestamp + this.anchorForwardMs
      filtered = filtered.filter((b) => b.timestamp <= cutoff)
    }
    const more = filtered.length > 0
    this.resetRealtimeBucketSeed()
    this.chart.applyNewData(filtered.map(toKLineData), more)
    this.eventBus.emit('data:loaded', { bars: filtered, symbol: symbol.ticker })
    this.evaluateInitialExhaustion(filtered)
  }

  /**
   * Initial (most-recent page) load for `(symbol, period)`. Resolves `true`
   * once bars were applied (an empty-but-successful response counts), `false`
   * when the load was superseded, aborted, or failed for good. A transient
   * failure gets a bounded retry (`MAX_INITIAL_LOAD_RETRIES`) before the
   * single `data:error` goes out — a period switch keeps the previous bars on
   * screen behind the host's veil, so a 503 from a saturated financial-api no
   * longer leaves the chart empty with nothing ever re-fetching.
   */
  async loadHistory(symbol: EdgeSymbol, period: Period): Promise<boolean> {
    if (!this.chart) return false

    // Staleness token. In-place symbol/anchor swaps (no remount, no clearData)
    // mean two loadHistory calls can be in flight at once — e.g. a history-row
    // click fires a symbol load and an anchored load in the same commit. The
    // bars apply wholesale via applyNewData, so whichever response lands LAST
    // would win regardless of which request is current; this token makes a
    // superseded load drop its response instead of clobbering the newer one.
    const token = ++this.loadHistoryToken
    this.loadHistoryAbort?.abort()
    const abort = new AbortController()
    this.loadHistoryAbort = abort

    // Record the active symbol/period up front, subscription-independent. The
    // realtime `subscribe()` path also sets these, but ANCHORED loads (focus
    // timestamp / bar replay) deliberately skip subscribe — so without this,
    // `currentSymbol`/`currentPeriod` stay null after `unsubscribe()` and the
    // replay forward-fill guard in `fetchForwardBars` returns [] for every
    // window (the fill never reaches the datafeed). `applyPreloadedBars` sets
    // them for the same reason; keep the two load paths symmetric.
    this.currentSymbol = symbol
    this.currentPeriod = period

    let from: number
    let to: number

    // Both anchor and non-anchor branches request a fixed-size most-recent
    // page sized by `pageMsFromBars`. Anchor mode ends the window at the
    // focus date; non-anchor mode ends it at "now". Older bars are paged in
    // by the scroll-back loop in `setupLoadDataCallback` and stop at the
    // closer of the per-ticker floor and the soft lookback cap.
    if (this.anchorTimestamp != null) {
      // A multi-day window inflates the reach to its span; single-day windows
      // (minAnchorLookbackMs = 0) keep the plain page size, unchanged. Only this
      // anchored branch grows — never `pageMsFromBars`, so scroll-back pages and
      // non-anchored charts everywhere are untouched.
      from = this.anchorTimestamp - Math.max(pageMsFromBars(period), this.minAnchorLookbackMs)
      // Pinned at the event day by default (anchorForwardMs = 0); a forward
      // outcome window extends the fetch past it to include D[2]+ bars.
      to = this.anchorTimestamp + this.anchorForwardMs
    } else {
      to = Date.now()
      from = to - pageMsFromBars(period)
    }

    /** True once this load is no longer the one the chart is waiting on. */
    const stale = () => !this.chart || token !== this.loadHistoryToken || abort.signal.aborted

    let lastError: unknown = null
    for (let attempt = 0; attempt <= MAX_INITIAL_LOAD_RETRIES; attempt++) {
      if (attempt > 0) {
        const backoff =
          INITIAL_LOAD_RETRY_BACKOFF_MS[
            Math.min(attempt - 1, INITIAL_LOAD_RETRY_BACKOFF_MS.length - 1)
          ]
        await sleepUnlessAborted(backoff, abort.signal)
        // A supersede/teardown during the backoff must not fire the retry.
        if (stale()) return false
      }
      try {
        const bars = await this.datafeed.getHistoryBars(
          symbol,
          period,
          from,
          to,
          this.buildHistoryBarsOpts({ signal: abort.signal }),
        )

        if (stale()) return false // superseded while in flight

        // Hard cutoff: any bar with ts < floor is dangling/bad data — reject.
        let filtered = this.filterBelowFloor(bars, symbol.ticker, period.label)
        filtered = this.applyBarFilter(filtered)
        filtered = this.suppressFormingDailyBar(filtered)
        // Trim bars past the event day — except the forward outcome window, which
        // deliberately extends the anchored view ahead by `anchorForwardMs` (0 by
        // default, so this stays a strict event-day cutoff for every other chart).
        if (this.anchorTimestamp != null) {
          const cutoff = this.anchorTimestamp + this.anchorForwardMs
          filtered = filtered.filter((b) => b.timestamp <= cutoff)
        }
        const more = filtered.length > 0
        this.resetRealtimeBucketSeed()
        this.chart!.applyNewData(filtered.map(toKLineData), more)
        this.eventBus.emit('data:loaded', { bars: filtered, symbol: symbol.ticker })
        this.evaluateInitialExhaustion(filtered)
        return true
      } catch (err) {
        // Our own cancellation (destroy / superseded): nothing to report, and
        // nothing to retry — the newer load owns the chart now.
        if (stale()) return false
        lastError = err
      }
    }
    this.eventBus.emit('data:error', {
      error: lastError instanceof Error ? lastError : new Error(String(lastError)),
      context: 'loadHistory',
    })
    return false
  }

  /**
   * Set `reason='floor'` after an initial-load (or cache-hit) when we know
   * we got everything: firstBar.timestamp ≤ floor, or empty + known floor.
   * Empty + unknown floor is ambiguous (transient vs thin ticker) — left to
   * the scroll-back loop to decide.
   */
  private evaluateInitialExhaustion(bars: EdgeBar[]): void {
    if (bars.length === 0) {
      if (this.floor != null) {
        this.setScrollBackExhaustedReason('floor')
      }
      return
    }
    if (this.floor != null && bars[0].timestamp <= this.floor) {
      this.floorReached = true
      this.setScrollBackExhaustedReason('floor')
    }
  }

  /**
   * Seed for merging realtime bars into the bucket the chart ALREADY holds
   * from history. The stream only aggregates ticks observed since subscribe;
   * when the chart's last historical bar is the same still-forming bucket,
   * a raw `updateData` would REPLACE it and collapse the candle to the
   * stream's window (the 15m-chart "last bar overwritten" bug). The base is
   * captured ONCE per bucket, on the first stream bar for that bucket, and
   * every later stream update merges against this frozen snapshot (merging
   * against the chart's live last bar would compound our own emits).
   * `realtimeBucketTs` records which bucket the decision was made for -
   * including "no base" for buckets the stream itself created.
   */
  private realtimeBase: {
    ts: number
    open: number
    high: number
    low: number
    volume: number
  } | null = null
  private realtimeBucketTs: number | null = null

  /**
   * Merge a realtime bar with the chart's loaded last bar when both describe
   * the same forming bucket; pass through otherwise.
   *
   * Volume caveat: the seed minute can be double-counted by up to one partial
   * minute - history's last bar may already include part of the minute in
   * flight at fetch time, and the stream re-reports that whole minute. Exact
   * decomposition is impossible client-side (history volume is not
   * minute-attributed). The error is bounded, biases high for one bucket
   * only, and the next history fetch restores server truth.
   */
  private mergeIntoLoadedBucket(bar: EdgeBar): EdgeBar {
    if (this.realtimeBucketTs !== bar.timestamp) {
      // First stream bar for this bucket: decide (once) whether it seeds from
      // an already-loaded bar. Never re-seed mid-bucket - after our first
      // emit, the chart's last bar IS our own merged output.
      this.realtimeBucketTs = bar.timestamp
      this.realtimeBase = null
      const list = this.chart?.getDataList() as unknown as readonly EdgeBar[] | undefined
      const last = list?.[list.length - 1]
      if (last && last.timestamp === bar.timestamp) {
        this.realtimeBase = {
          ts: last.timestamp,
          open: last.open,
          high: last.high,
          low: last.low,
          volume: last.volume ?? 0,
        }
      }
    }
    const base = this.realtimeBase
    if (!base) return bar
    return {
      ...bar,
      open: base.open,
      high: Math.max(base.high, bar.high),
      low: Math.min(base.low, bar.low),
      volume: base.volume + bar.volume,
    }
  }

  /** Forget the bucket seed: any full-series apply or (re)subscribe changes
   *  what "the chart's last bar" means, so the next stream bar re-decides. */
  private resetRealtimeBucketSeed(): void {
    this.realtimeBase = null
    this.realtimeBucketTs = null
  }

  subscribe(symbol: EdgeSymbol, period: Period): void {
    // Register the NEW subscriber first, then tear down the old one. The
    // realtime stream is one Ably channel per TICKER, ref-counted across
    // periods: unsubscribe-then-subscribe let the count touch zero on every
    // timeframe change, which closed the channel (and, with no channel left,
    // the whole Ably connection) only to reopen it a tick later. Capture the
    // previous registration before overwriting the live fields so it can be
    // torn down by its own key + callback (NOT the public `unsubscribe()`,
    // which also nulls currentSymbol/currentPeriod).
    const prevSymbol = this.subscribedSymbol
    const prevPeriod = this.subscribedPeriod
    const prevCallback = this.currentCallback
    this.resetRealtimeBucketSeed()
    this.currentSymbol = symbol
    this.currentPeriod = period

    const cb = (bar: EdgeBar) => {
      if (!this.chart) return
      if (!this.shouldKeepRealtimeBar(bar)) return
      // Premarket on a daily chart: keep feeding the y-axis price marker but
      // never materialize today's candle. The first regular-session tick
      // falls through, clears the marker, and starts the real daily bar.
      if (
        this.currentPeriod?.timespan === 'day' &&
        isPremarketFormingDailyBar(bar.timestamp, Date.now())
      ) {
        this.emitPremarketPrice(bar.close, bar.timestamp)
        return
      }
      this.emitPremarketPrice(null)
      const merged = this.mergeIntoLoadedBucket(bar)
      this.chart.updateData(toKLineData(merged))
      this.eventBus.emit('data:updated', { bar: merged, symbol: symbol.ticker })
    }
    this.currentCallback = cb
    this.subscribedSymbol = symbol
    this.subscribedPeriod = period
    this.datafeed.subscribe(symbol, period, cb, { regularSession: this.regularSessionBuckets() })
    if (prevSymbol && prevPeriod) {
      this.datafeed.unsubscribe(prevSymbol, prevPeriod, prevCallback ?? undefined)
    }
  }

  /**
   * Fetch bars for the active symbol in `[from, to]` using the same session
   * options (`market_session` / `adjusted`) as the initial load. Used by the
   * replay forward-fill so stepping can't leak AH bars into an RTH chart
   * (spec §"Session filter / extended hours"). Returns `[]` before a load has
   * established the current symbol/period.
   *
   * `periodOverride` lets sub-bar replay pull a FINER resolution than the
   * displayed period (spec Addendum §"Sub-bar playback") — e.g. 30m sub-bars
   * for a 1D chart — without disturbing the live `currentPeriod`. Session opts
   * are unchanged; only the bar resolution differs. Falls back to
   * `currentPeriod` when omitted (whole-bar replay).
   */
  async fetchForwardBars(from: number, to: number, periodOverride?: Period): Promise<EdgeBar[]> {
    if (!this.currentSymbol || !this.currentPeriod) {
      // Replay diagnostics (window.__chartDebug): a silent [] here means the
      // guard tripped, NOT that the server had no data — surface which.
      if ((window as unknown as Record<string, unknown>).__chartDebug) {
        console.log('[Replay] fetchForwardBars guard tripped', {
          symbol: this.currentSymbol?.ticker ?? null,
          period: this.currentPeriod?.label ?? null,
        })
      }
      return []
    }
    const bars = await this.datafeed.getHistoryBars(
      this.currentSymbol,
      periodOverride ?? this.currentPeriod,
      from,
      to,
      this.buildHistoryBarsOpts(),
    )
    if ((window as unknown as Record<string, unknown>).__chartDebug) {
      console.log('[Replay] fetchForwardBars', {
        ticker: this.currentSymbol.ticker,
        period: (periodOverride ?? this.currentPeriod).label,
        got: bars.length,
      })
    }
    return bars
  }

  /**
   * Apply a single replay bar via the same incremental `updateData` path a live
   * tick uses, then emit `data:updated` so OHLC-header / legend consumers track
   * the step for free (spec §"Stepping = chart.updateData").
   */
  applyReplayBar(bar: EdgeBar): void {
    if (!this.chart) return
    this.chart.updateData(toKLineData(bar))
    this.eventBus.emit('data:updated', {
      bar,
      symbol: this.currentSymbol?.ticker ?? '',
    })
  }

  async reload(symbol: EdgeSymbol, period: Period): Promise<void> {
    this.unsubscribe()
    if (this.chart) this.chart.clearData()
    // Chart buffer cleared → reset the scroll-back state machine, else stale
    // flags (floorReached / scrollBackExhaustedReason / lastScrollBackLeftmost)
    // block the next scroll-back. The generation bump also makes any dangling
    // IIFE bail instead of flipping flags / calling `callback([], false)` on the
    // new chart (which would silence klinecharts' Forward retries indefinitely).
    this.resetScrollbackState()
    await this.loadHistory(symbol, period)
    if (this.anchorTimestamp == null) {
      this.subscribe(symbol, period)
    }
  }

  unsubscribe(): void {
    this.resetRealtimeBucketSeed()
    // Unsubscribe by the key the callback was REGISTERED under, not
    // currentSymbol/currentPeriod (setSymbol/setPeriod may have already moved
    // those to the new target — see the field docs).
    if (this.subscribedSymbol && this.subscribedPeriod) {
      this.datafeed.unsubscribe(
        this.subscribedSymbol,
        this.subscribedPeriod,
        this.currentCallback ?? undefined,
      )
    }
    this.subscribedSymbol = null
    this.subscribedPeriod = null
    this.currentCallback = null
    this.currentSymbol = null
    this.currentPeriod = null
  }

  destroy(): void {
    this.unsubscribe()
    this.loadHistoryAbort?.abort()
    this.scrollBackAbort?.abort()
    this.chart = null
  }
}
